"use client";

import { useState, useEffect } from "react";

export interface TenantLimits {
  tenant_id: string;
  tenant_slug?: string;
  member_limit: number;
  storage_limit_gb: number;
  file_upload_limit_mb: number;
  allow_bulk_invites: boolean;
  allow_multiple_admins: boolean;
  allow_audit_logs: boolean;
  enable_events: boolean;
  enable_compliance_review: boolean;
  enable_dues_management: boolean;
  hide_subscriptions?: boolean;
}

export function useTenantLimits(tenantId?: string) {
  const [limits, setLimits] = useState<TenantLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setLoading(false);
      return;
    }

    const fetchLimits = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch("/api/tenant/limits", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tenantId }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to fetch tenant limits");
        }

        const data = await response.json();
        setLimits(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
        // Set default limits on error
        setLimits({
          tenant_id: tenantId,
          member_limit: 1,
          storage_limit_gb: 1,
          file_upload_limit_mb: 50,
          allow_bulk_invites: false,
          allow_multiple_admins: false,
          allow_audit_logs: false,
          enable_events: false,
          enable_compliance_review: false,
          enable_dues_management: false,
          hide_subscriptions: false,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchLimits();
  }, [tenantId]);

  return { limits, loading, error };
}